import { DocumentLandscapeData20Regular, WebAsset20Regular } from '@fluentui/react-icons'
import { Suspense, useCallback } from 'react'
import { useRestClient } from '@/contexts/QueryProvider'
import { AppEntityTypeFormV2 } from '../Forms/AppEntityTypeFormV2'
import { Button } from '@/components/shadcn/button'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { useCurrentApplication } from '../useCurrentApplication'
import { EntityTypeCard } from './EntityTypeCard'
import { EntityCardActionsMenuV2 } from './EntityTypeCard/EntityCardActionsMenuV2'
import { FileTemplatesList } from './EntityTypeCard/FileTemplatesList'
import { DataAssetsList } from './EntityTypeCard/DataAssetsList'
import { ComposedPopoverV2 } from '@/components/ui/ComposedPopoverV2'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { LoadingComponent } from '@/components/ui/LoadingComponent'

export const AppEntityTypesSection = () => {
  const { getEntityTypeApiGetEntityTypesByApplicationId } = useRestClient()
  const { applicationId, appName } = useCurrentApplication()

  const invalidateEntityTypes = useCallback(() => {
    getEntityTypeApiGetEntityTypesByApplicationId.invalidateQueries({ parameters: { path: { applicationId } } })
  }, [applicationId, getEntityTypeApiGetEntityTypesByApplicationId])

  return (
    <SectionContainer
      title="Entity types"
      action={
        <AppEntityTypeFormV2
          sheetTitle={`${appName} > Add entity types`}
          sheetDescription="Add a new entity type to your application."
          sheetTriggerTooltip="Add entity types"
          onSubmit={invalidateEntityTypes}
          sheetTrigger={
            <Button variant="ghost" size="icon">
              <IconPlusOutline />
              <span className="sr-only">Add Entity Type</span>
            </Button>
          }
        />
      }
    >
      <QueryErrorBoundary errorMessage="Failed to fetch entity types">
        <Suspense fallback={<LoadingComponent text="Loading entity types..." />}>
          <AppEntityTypesList />
        </Suspense>
      </QueryErrorBoundary>
    </SectionContainer>
  )
}

function AppEntityTypesList() {
  const { getEntityTypeApiGetEntityTypesByApplicationId } = useRestClient()
  const { applicationId } = useCurrentApplication()
  const { data: entityTypes } = getEntityTypeApiGetEntityTypesByApplicationId.useQuery({
    path: { applicationId },
  })

  return (
    <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-1">
      {entityTypes?.map((entityType) => (
        <EntityTypeCard
          key={entityType.id}
          name={entityType.name}
          description={entityType.description}
          cardMenu={<EntityCardActionsMenuV2 entityType={entityType} />}
          fileTemplatesListPopover={
            <ComposedPopoverV2
              className="max-h-[500px] w-fit overflow-y-auto"
              trigger={
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <WebAsset20Regular />
                  <span>File templates</span>
                </Button>
              }
            >
              <FileTemplatesList entityType={entityType} applicationId={applicationId} />
            </ComposedPopoverV2>
          }
          dataAssetsListPopover={
            <ComposedPopoverV2
              className="max-h-[500px] w-fit overflow-y-auto"
              trigger={
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <DocumentLandscapeData20Regular />
                  <span>Data assets</span>
                </Button>
              }
            >
              <DataAssetsList entityType={entityType} applicationId={applicationId} />
            </ComposedPopoverV2>
          }
        />
      ))}
    </div>
  )
}
